/**
 * Countries Tab - Country CRUD for marketplace settings
 */

import React from 'react';
import { Plus, Trash2, GripVertical, Globe } from 'lucide-react';
import type { CustomMarketplace, CustomMarketplaceCountry, CategoryColumnDefaults } from '../types';
import {
  deleteCategoryDefaultsByCountry,
  getCategoryDefaultsByMarketplace,
} from '../../../utils/database';

interface CountriesTabProps {
  selectedMarketplace: CustomMarketplace;
  setSelectedMarketplace: (mp: CustomMarketplace) => void;
  setCategoryDefaults: (defaults: CategoryColumnDefaults[] | ((prev: CategoryColumnDefaults[]) => CategoryColumnDefaults[])) => void;
}

const CountriesTab: React.FC<CountriesTabProps> = ({
  selectedMarketplace,
  setSelectedMarketplace,
  setCategoryDefaults,
}) => {
  const handleAddCountry = () => {
    const newCountry: CustomMarketplaceCountry = {
      code: 'XX',
      name: 'New Country',
      currency: 'USD',
      currencySymbol: '$',
      taxRate: 0,
      taxName: 'Tax',
      flag: '\u{1F3F3}\uFE0F',
      isActive: true,
    };

    setSelectedMarketplace({
      ...selectedMarketplace,
      countries: [...selectedMarketplace.countries, newCountry],
    });
  };

  const handleUpdateCountry = (index: number, field: keyof CustomMarketplaceCountry, value: string | number | boolean) => {
    const updatedCountries = [...selectedMarketplace.countries];
    updatedCountries[index] = { ...updatedCountries[index], [field]: value };

    setSelectedMarketplace({
      ...selectedMarketplace,
      countries: updatedCountries,
    });
  };

  const handleDeleteCountry = (index: number) => {
    setSelectedMarketplace({
      ...selectedMarketplace,
      countries: selectedMarketplace.countries.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-slate-700">&#xDC;lkeler / Marketler</h3>
        <button
          onClick={handleAddCountry}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          &#xDC;lke Ekle
        </button>
      </div>

      {selectedMarketplace.countries.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Globe className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p>Hen&#xFC;z &#xFC;lke eklenmemi&#x15F;</p>
          <button
            onClick={handleAddCountry}
            className="mt-2 text-indigo-600 hover:underline"
          >
            &#x130;lk &#xFC;lkeyi ekle
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {selectedMarketplace.countries.map((country, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
            >
              <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
              <input
                type="text"
                value={country.flag}
                onChange={(e) => handleUpdateCountry(index, 'flag', e.target.value)}
                className="w-12 px-2 py-1 text-center border border-slate-200 rounded"
                maxLength={2}
              />
              <input
                type="text"
                value={country.code}
                onChange={(e) => handleUpdateCountry(index, 'code', e.target.value.toUpperCase())}
                className="w-16 px-2 py-1 text-center border border-slate-200 rounded font-mono"
                maxLength={3}
                placeholder="Code"
              />
              <input
                type="text"
                value={country.name}
                onChange={(e) => handleUpdateCountry(index, 'name', e.target.value)}
                className="flex-1 px-3 py-1 border border-slate-200 rounded"
                placeholder="Country Name"
              />
              <input
                type="text"
                value={country.currency}
                onChange={(e) => handleUpdateCountry(index, 'currency', e.target.value.toUpperCase())}
                className="w-16 px-2 py-1 text-center border border-slate-200 rounded font-mono"
                maxLength={3}
                placeholder="USD"
              />
              <input
                type="text"
                value={country.currencySymbol}
                onChange={(e) => handleUpdateCountry(index, 'currencySymbol', e.target.value)}
                className="w-12 px-2 py-1 text-center border border-slate-200 rounded"
                maxLength={2}
                placeholder="$"
              />
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={country.isActive}
                  onChange={(e) => handleUpdateCountry(index, 'isActive', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded"
                />
                Aktif
              </label>
              <button
                onClick={async () => {
                  if (window.confirm(`${country.name} i\u00E7in t\u00FCm gider oranlar\u0131 da silinecek. Devam?`)) {
                    await deleteCategoryDefaultsByCountry(selectedMarketplace.id, country.code);
                    handleDeleteCountry(index);
                    const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
                    setCategoryDefaults(defaults);
                  }
                }}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountriesTab;
